import { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { api } from '../lib/api';
import { formatUsd, formatDate, shortId } from '../lib/format';
import { SupportCasesTable } from '../components/SupportCasesTable';
import { OrderHoldPanel, holdState } from '../components/OrderHold';
import {
  CopyableId,
  ExternalButton,
  OrderExternalLinks,
  StripeCustomerLink,
} from '../components/ExternalLinks';
import { GELATO_ORDERS_URL, stripeDisputeUrl } from '../lib/external';
import { FRAME_OPTIONS } from '@lumina/shared';
import { TestBadge } from '../components/TestBadge';
import { DisputeBadge } from '../components/DisputeBadge';

interface GelatoLiveOrder {
  id: string;
  order_type: string | null;
  fulfillment_status: string | null;
  financial_status: string | null;
  updated_at: string | null;
  items: Array<{ reference: string | null; product_uid: string | null; quantity: number | null; fulfillment_status: string | null }>;
  shipment: { method: string | null; min_delivery_days: number | null; max_delivery_days: number | null } | null;
  total: number | null;
  currency: string | null;
}

interface OrderDetailResponse {
  order: {
    id: number;
    uuid: string;
    customer_email: string;
    customer_first_name: string | null;
    customer_last_name: string | null;
    customer_uuid: string;
    subtotal: number;
    tax: number;
    shipping_cost: number;
    discount: number;
    promo_code: string | null;
    total: number;
    status: string;
    payment_status: string;
    fulfillment_status: string;
    stripe_checkout_session_id: string | null;
    stripe_payment_intent_id: string | null;
    stripe_customer_id: string | null;
    gelato_order_id: string | null;
    gelato_dispatch_pending: number;
    is_test: number;
    review_required: number;
    approved_at: string | null;
    approved_by: string | null;
    funds_required: number;
    funds_status: string | null;
    funds_available_on: string | null;
    funds_checked_at: string | null;
    released_early_by: string | null;
    customer_notes: string | null;
    internal_notes: string | null;
    created_at: string;
    paid_at: string | null;
    shipped_at: string | null;
    completed_at: string | null;
    items: Array<{
      product_name: string;
      size_id: string;
      frame_id: string;
      quantity: number;
      unit_price: number;
      total_price: number;
      photo_src: string;
      /** Gelato unit cost snapshotted at purchase; null if unknown then. */
      unit_cost_cents: number | null;
      size_label: string | null;
    }>;
    transactions: Array<{ uuid: string; type: string; amount: number; created_at: string }>;
    shipments: Array<{
      uuid: string;
      tracking_number: string | null;
      tracking_url: string | null;
      carrier: string | null;
      status: string;
      shipped_at: string | null;
      delivered_at: string | null;
    }>;
    disputes: Array<{
      uuid: string;
      stripe_dispute_id: string;
      amount: number;
      reason: string | null;
      status: string;
      evidence_due_by: string | null;
      is_test: number;
      created_at: string;
      closed_at: string | null;
    }>;
    shipping_address: {
      name: string;
      street1: string;
      street2: string | null;
      city: string;
      state: string;
      postal_code: string;
      country: string;
    } | null;
  };
}

export function OrderDetail() {
  const { uuid } = useParams();
  const [data, setData] = useState<OrderDetailResponse | null>(null);
  const [notes, setNotes] = useState('');
  const [busy, setBusy] = useState(false);

  const load = () =>
    api
      .get<OrderDetailResponse>(`/api/admin/orders/${uuid}`)
      .then((d) => {
        setData(d);
        setNotes(d.order.internal_notes || '');
      })
      .catch(console.error);

  useEffect(() => {
    load();
  }, [uuid]);

  if (!data) return <p>Loading…</p>;
  const o = data.order;
  const FRAME_LABELS: Record<string, string> = Object.fromEntries(FRAME_OPTIONS.map((f) => [f.id, f.label]));
  // Product cost only covers lines whose cost was known at purchase.
  const costedItems = o.items.filter((it) => it.unit_cost_cents != null);
  const productCost = costedItems.reduce((sum, it) => sum + it.unit_cost_cents! * it.quantity, 0);
  const costedRevenue = costedItems.reduce((sum, it) => sum + it.total_price, 0);
  const allCosted = costedItems.length === o.items.length;

  const saveNotes = async () => {
    setBusy(true);
    try {
      await api.put(`/api/admin/orders/${o.uuid}`, { internal_notes: notes });
    } finally {
      setBusy(false);
    }
  };

  const retryGelato = async () => {
    setBusy(true);
    try {
      await api.post(`/api/admin/orders/${o.uuid}/dispatch`);
      await load();
    } catch (err) {
      alert(`Retry failed: ${(err as Error).message}`);
    } finally {
      setBusy(false);
    }
  };

  const markShipped = async () => {
    const trackingNumber = prompt('Tracking number (optional):') || '';
    const carrier = prompt('Carrier (optional):') || '';
    setBusy(true);
    try {
      await api.post(`/api/admin/orders/${o.uuid}/mark-shipped`, {
        tracking_number: trackingNumber || null,
        carrier: carrier || null,
      });
      await load();
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <Link to="/orders" style={{ color: '#6b7280', fontSize: 13 }}>
        ← Orders
      </Link>
      <h1 style={{ fontFamily: 'Georgia, serif', fontSize: 28, fontWeight: 400, margin: '8px 0 4px' }}>
        Order #{shortId(o.uuid)}
      </h1>
      <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap', margin: '0 0 24px' }}>
        <p style={{ color: '#6b7280', margin: 0 }}>
          Placed {formatDate(o.created_at)} · <Link to={`/customers/${o.customer_uuid}`}>{o.customer_email}</Link>
        </p>
        <OrderExternalLinks order={o} />
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '2fr 1fr', gap: 24 }}>
        <div>
          <Section title="Items">
            {o.items.map((it, i) => (
              <div
                key={i}
                style={{
                  display: 'grid',
                  gridTemplateColumns: '60px 1fr 60px 120px',
                  gap: 16,
                  padding: '12px 0',
                  borderBottom: '1px solid #f0f0f0',
                  alignItems: 'center',
                }}
              >
                <img
                  src={it.photo_src}
                  alt=""
                  style={{ width: 60, height: 60, objectFit: 'cover', borderRadius: 4 }}
                />
                <div>
                  <div>{it.product_name}</div>
                  <div style={{ fontSize: 12, color: '#6b7280' }}>
                    {it.size_label ?? it.size_id} · {FRAME_LABELS[it.frame_id] ?? it.frame_id}
                  </div>
                </div>
                <span style={{ textAlign: 'center' }}>×{it.quantity}</span>
                <span style={{ textAlign: 'right' }}>
                  {formatUsd(it.total_price)}
                  <span style={{ display: 'block', fontSize: 11, color: '#6b7280' }}>
                    {it.unit_cost_cents != null
                      ? `cost ${formatUsd(it.unit_cost_cents * it.quantity)}`
                      : 'cost unknown'}
                  </span>
                </span>
              </div>
            ))}
            <div style={{ marginTop: 16 }}>
              <Row label="Subtotal" value={formatUsd(o.subtotal)} />
              {o.discount > 0 && (
                <Row
                  label={o.promo_code ? `Discount (${o.promo_code})` : 'Discount'}
                  value={`−${formatUsd(o.discount)}`}
                />
              )}
              <Row label="Shipping" value={formatUsd(o.shipping_cost)} />
              <Row label="Tax" value={formatUsd(o.tax)} />
              <Row label="Total" value={formatUsd(o.total)} bold />
            </div>
            <div style={{ marginTop: 12, paddingTop: 12, borderTop: '1px dashed #e5e5e5' }}>
              <Row
                label={allCosted ? 'Product cost (Gelato)' : `Product cost (${costedItems.length}/${o.items.length} lines known)`}
                value={costedItems.length ? formatUsd(productCost) : '—'}
              />
              <Row
                label="Gross margin on prints"
                value={
                  costedItems.length
                    ? `${formatUsd(costedRevenue - productCost)} (${Math.round(((costedRevenue - productCost) / costedRevenue) * 100)}%)`
                    : '—'
                }
              />
              <p style={{ fontSize: 11, color: '#9ca3af', margin: '4px 0 0' }}>
                Before Gelato shipping, Stripe fees and tax.
              </p>
            </div>
          </Section>

          <div style={{ marginBottom: 16 }}>
            <SupportCasesTable
              params={new URLSearchParams({ order: o.uuid })}
              emptyText="No support cases for this order."
            />
          </div>

          <Section title="Internal notes">
            <textarea
              value={notes}
              onChange={(e) => setNotes(e.target.value)}
              rows={4}
              style={{
                width: '100%',
                padding: 10,
                border: '1px solid #e5e5e5',
                borderRadius: 6,
                fontFamily: 'inherit',
                fontSize: 14,
              }}
            />
            <button onClick={saveNotes} disabled={busy} style={btnPrimary}>
              Save notes
            </button>
          </Section>
        </div>

        <div>
          {o.disputes.length > 0 && (
            <Section title="Dispute">
              {o.disputes.map((d) => {
                const open = !['won', 'lost', 'warning_closed'].includes(d.status);
                return (
                  <div key={d.uuid} style={{ marginBottom: 12 }}>
                    <p style={{ margin: '0 0 8px' }}>
                      <DisputeBadge status={d.status} />
                    </p>
                    <Row label="Status" value={d.status.replace(/_/g, ' ')} />
                    <Row label="Amount" value={formatUsd(d.amount)} />
                    <Row label="Reason" value={(d.reason ?? 'not given').replace(/_/g, ' ')} />
                    {open && d.evidence_due_by && (
                      <Row label="Respond by" value={formatDate(d.evidence_due_by)} bold />
                    )}
                    <Row label="Opened" value={formatDate(d.created_at)} />
                    {d.closed_at && <Row label="Closed" value={formatDate(d.closed_at)} />}
                    {open && (
                      <p style={{ fontSize: 13, color: '#991b1b', margin: '8px 0' }}>
                        Dispatch to Gelato is blocked until this is resolved.
                      </p>
                    )}
                    <ExternalButton
                      href={stripeDisputeUrl(d.stripe_dispute_id, d.is_test ? 'test' : 'live')}
                      color="#635bff"
                    >
                      {open ? 'Respond in Stripe' : 'View in Stripe'}
                    </ExternalButton>
                  </div>
                );
              })}
            </Section>
          )}
          <Section title="Status">
            <Row label="Order" value={o.status} />
            <Row label="Payment" value={o.payment_status} />
            <Row label="Fulfillment" value={o.fulfillment_status} />
            <Row label="Paid" value={formatDate(o.paid_at)} />
            <Row label="Shipped" value={formatDate(o.shipped_at)} />
            {o.is_test === 1 && (
              <p style={{ fontSize: 13, color: '#92400e', margin: '12px 0 0' }}>
                <TestBadge /> Paid with Stripe test keys. Held from Gelato; sending it
                creates a draft that is never printed.
              </p>
            )}
            <OrderHoldPanel order={o} orderUuid={o.uuid} total={o.total} onChanged={load} />
            {o.gelato_dispatch_pending === 1 && !holdState(o).held && (
              <div style={{ marginTop: 12 }}>
                {o.is_test !== 1 && (
                  <p style={{ color: '#b91c1c', fontSize: 13, margin: '0 0 8px' }}>
                    Gelato dispatch failed.
                  </p>
                )}
                <button onClick={retryGelato} disabled={busy} style={btnPrimary}>
                  {o.is_test === 1 ? 'Send to Gelato as draft' : 'Retry Gelato'}
                </button>
              </div>
            )}
            {o.status === 'processing' && o.gelato_dispatch_pending !== 1 && (
              <button onClick={markShipped} disabled={busy} style={{ ...btnSecondary, marginTop: 12 }}>
                Mark shipped manually
              </button>
            )}
          </Section>

          <Section title="Shipping address">
            {o.shipping_address ? (
              <address style={{ fontStyle: 'normal', fontSize: 14, lineHeight: 1.6 }}>
                {o.shipping_address.name}
                <br />
                {o.shipping_address.street1}
                {o.shipping_address.street2 && (
                  <>
                    <br />
                    {o.shipping_address.street2}
                  </>
                )}
                <br />
                {o.shipping_address.city}, {o.shipping_address.state} {o.shipping_address.postal_code}
                <br />
                {o.shipping_address.country}
              </address>
            ) : (
              <p style={{ color: '#6b7280' }}>No shipping address recorded.</p>
            )}
          </Section>

          <Section title="Stripe">
            {o.stripe_payment_intent_id || o.stripe_checkout_session_id ? (
              <>
                {o.stripe_payment_intent_id && (
                  <RefRow label="Payment" value={<CopyableId value={o.stripe_payment_intent_id} />} />
                )}
                {o.stripe_checkout_session_id && (
                  <RefRow label="Checkout session" value={<CopyableId value={o.stripe_checkout_session_id} />} />
                )}
                {o.stripe_customer_id && (
                  <RefRow label="Customer" value={<CopyableId value={o.stripe_customer_id} />} />
                )}
                <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
                  <OrderExternalLinks order={{ ...o, gelato_order_id: null }} stripeLabel="Payment" />
                  {o.stripe_customer_id && (
                    <StripeCustomerLink
                      customerId={o.stripe_customer_id}
                      checkoutSessionId={o.stripe_checkout_session_id}
                      label="Customer"
                    />
                  )}
                </div>
              </>
            ) : (
              <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>No Stripe references.</p>
            )}
          </Section>

          <GelatoSection orderUuid={o.uuid} gelatoOrderId={o.gelato_order_id} />

          {o.shipments.length > 0 && (
            <Section title="Shipments">
              {o.shipments.map((s) => (
                <div key={s.uuid} style={{ marginBottom: 8 }}>
                  <Row label="Carrier" value={s.carrier || '—'} />
                  <Row label="Tracking" value={s.tracking_number || '—'} />
                  <Row label="Status" value={s.status} />
                  {s.tracking_url && (
                    <a href={s.tracking_url} target="_blank" rel="noreferrer" style={{ fontSize: 13 }}>
                      Track →
                    </a>
                  )}
                </div>
              ))}
            </Section>
          )}
        </div>
      </div>
    </div>
  );
}

// Our record of the Gelato order plus, on demand, Gelato's own live view of it.
function GelatoSection({ orderUuid, gelatoOrderId }: { orderUuid: string; gelatoOrderId: string | null }) {
  const [live, setLive] = useState<GelatoLiveOrder | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);

  const fetchLive = async () => {
    setLoading(true);
    setError(null);
    try {
      const d = await api.get<{ gelato: GelatoLiveOrder }>(`/api/admin/orders/${orderUuid}/gelato`);
      setLive(d.gelato);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section title="Gelato">
      {gelatoOrderId ? (
        <>
          <RefRow label="Order" value={<CopyableId value={gelatoOrderId} />} />
          <div style={{ display: 'flex', gap: 8, marginTop: 10, flexWrap: 'wrap' }}>
            <OrderExternalLinks
              order={{ stripe_checkout_session_id: null, stripe_payment_intent_id: null, gelato_order_id: gelatoOrderId }}
            />
            <ExternalButton href={GELATO_ORDERS_URL} color="#0f766e">
              Gelato order list
            </ExternalButton>
            <button type="button" onClick={fetchLive} disabled={loading} style={btnSecondary}>
              {loading ? 'Checking…' : live ? 'Refresh status' : 'Check status in Gelato'}
            </button>
          </div>
          {error && <p style={{ color: '#b91c1c', fontSize: 13, margin: '10px 0 0' }}>{error}</p>}
          {live && (
            <div style={{ marginTop: 12, borderTop: '1px solid #f0f0f0', paddingTop: 8 }}>
              <Row label="Fulfillment" value={live.fulfillment_status ?? '—'} />
              <Row label="Financial" value={live.financial_status ?? '—'} />
              {live.order_type && <Row label="Type" value={live.order_type} />}
              {live.shipment?.method && <Row label="Shipping" value={live.shipment.method} />}
              {live.total !== null && (
                <Row label="Gelato charge" value={`${live.total} ${live.currency ?? ''}`.trim()} />
              )}
              {live.items.map((i, idx) => (
                <Row
                  key={idx}
                  label={`Item ${i.reference ?? idx + 1}`}
                  value={`${i.quantity ?? '?'}× · ${i.fulfillment_status ?? '—'}`}
                />
              ))}
              {live.updated_at && <Row label="Updated" value={formatDate(live.updated_at)} />}
            </div>
          )}
        </>
      ) : (
        <p style={{ color: '#6b7280', fontSize: 13, margin: 0 }}>Not sent to Gelato yet.</p>
      )}
    </Section>
  );
}

function RefRow({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: 14 }}>
      <span style={{ color: '#6b7280' }}>{label}</span>
      {value}
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ background: '#fff', border: '1px solid #e5e5e5', borderRadius: 8, padding: 20, marginBottom: 16 }}>
      <h2
        style={{
          margin: '0 0 12px',
          fontSize: 12,
          letterSpacing: 1,
          textTransform: 'uppercase',
          color: '#6b7280',
        }}
      >
        {title}
      </h2>
      {children}
    </div>
  );
}

function Row({ label, value, bold }: { label: string; value: string; bold?: boolean }) {
  return (
    <div
      style={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '4px 0',
        fontSize: 14,
        fontWeight: bold ? 600 : 400,
      }}
    >
      <span style={{ color: bold ? '#1a1a1a' : '#6b7280' }}>{label}</span>
      <span>{value}</span>
    </div>
  );
}

const btnPrimary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#1a1a1a',
  color: '#fff',
  border: 'none',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
  marginTop: 8,
};

const btnSecondary: React.CSSProperties = {
  padding: '8px 14px',
  background: '#fff',
  color: '#1a1a1a',
  border: '1px solid #e5e5e5',
  borderRadius: 6,
  fontSize: 13,
  cursor: 'pointer',
};
